// Hobbies Poll Component
// Interactive voting poll with countdown timer and real-time updates

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, 
  Users, 
  Vote, 
  CheckCircle, 
  AlertCircle,
  Trophy,
  TrendingUp,
  BarChart3,
  Heart,
  Star,
  Zap
} from 'lucide-react';
import { useRealtime } from '../contexts/RealtimeContext';
import { listenToPoll, voteOnPoll, listenToPollVotes } from '../firebase/polls';
import toast from 'react-hot-toast';

const HobbiesPoll = ({ pollId }) => {
  const { currentUser } = useRealtime();
  const [poll, setPoll] = useState(null);
  const [votes, setVotes] = useState({});
  const [userVote, setUserVote] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isVoting, setIsVoting] = useState(false);
  const [isExpired, setIsExpired] = useState(false);

  // Calculate time remaining
  const calculateTimeLeft = useCallback((endTime) => {
    const now = new Date().getTime();
    const end = new Date(endTime).getTime();
    const difference = end - now;
    
    if (difference <= 0) {
      setIsExpired(true);
      return 0;
    }
    
    return difference;
  }, []);

  // Format time display
  const formatTime = (milliseconds) => {
    const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
    const hours = Math.floor((milliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Load poll data
  useEffect(() => {
    if (!pollId) return;

    const unsubscribe = listenToPoll(pollId, (result) => {
      if (result.success) {
        setPoll(result.data);
        setIsLoading(false);
        
        // Calculate initial time left
        if (result.data.endTime) {
          const timeRemaining = calculateTimeLeft(result.data.endTime);
          setTimeLeft(timeRemaining);
          setIsExpired(timeRemaining <= 0);
        }
      } else {
        setIsLoading(false);
      }
    });

    return unsubscribe;
  }, [pollId, calculateTimeLeft]);

  // Load votes data
  useEffect(() => {
    if (!pollId) return;

    const unsubscribe = listenToPollVotes(pollId, (result) => {
      if (result.success) {
        setVotes(result.data);
        
        // Check if current user has voted
        if (currentUser) {
          const votesArray = Array.isArray(result.data.votes) ? result.data.votes : [];
          const userVoteData = votesArray.find(vote => vote.userId === currentUser.uid);
          setUserVote(userVoteData?.optionId || null);
        }
      }
    });

    return unsubscribe;
  }, [pollId, currentUser]);

  // Countdown timer
  useEffect(() => {
    if (!poll?.endTime || isExpired) return;

    const timer = setInterval(() => {
      const timeRemaining = calculateTimeLeft(poll.endTime);
      setTimeLeft(timeRemaining);
      
      if (timeRemaining <= 0) {
        setIsExpired(true);
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [poll?.endTime, calculateTimeLeft, isExpired]);

  // Handle voting
  const handleVote = async (optionId) => {
    if (!currentUser) {
      toast.error('Please login to vote');
      return;
    }

    if (isExpired) {
      toast.error('This poll has expired');
      return;
    }

    if (userVote) {
      toast.error('You have already voted');
      return;
    }

    setIsVoting(true);
    try {
      const result = await voteOnPoll(pollId, currentUser.uid, optionId);
      if (result.success) {
        toast.success('Vote recorded successfully!');
        setUserVote(optionId);
      } else {
        toast.error('Failed to record vote');
      }
    } catch (error) {
      toast.error('Error recording vote');
    } finally {
      setIsVoting(false);
    }
  };

  // Calculate vote percentages
  const getVotePercentage = (optionId) => {
    const votesArray = Array.isArray(votes.votes) ? votes.votes : [];
    const totalVotes = votesArray.length;
    if (totalVotes === 0) return 0;
    
    const optionVotes = votesArray.filter(vote => vote.optionId === optionId).length;
    return Math.round((optionVotes / totalVotes) * 100);
  };

  // Get winning option
  const getWinningOption = () => {
    const votesArray = Array.isArray(votes.votes) ? votes.votes : [];
    if (!poll?.options || votesArray.length === 0) return null;
    
    const voteCounts = {};
    poll.options.forEach(option => {
      voteCounts[option.id] = votesArray.filter(vote => vote.optionId === option.id).length;
    });
    
    return Object.keys(voteCounts).reduce((a, b) => voteCounts[a] > voteCounts[b] ? a : b);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading poll...</span>
      </div>
    );
  }

  if (!poll) {
    return (
      <div className="text-center p-8">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Poll not found
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          The requested poll could not be found.
        </p>
      </div>
    );
  }

  const winningOptionId = getWinningOption();
  const votesArray = Array.isArray(votes.votes) ? votes.votes : [];
  const totalVotes = votesArray.length;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2 flex items-center">
              <Heart className="h-6 w-6 mr-2" />
              {poll.title}
            </h2>
            <p className="text-purple-100">{poll.description}</p>
          </div>
          
          <div className="text-right">
            <div className="flex items-center text-purple-100 mb-2">
              <Users className="h-4 w-4 mr-1" />
              <span className="text-sm">{totalVotes} votes</span>
            </div>
            
            {!isExpired ? (
              <div className="flex items-center text-purple-100">
                <Clock className="h-4 w-4 mr-1" />
                <span className="text-sm font-medium">
                  {formatTime(timeLeft)}
                </span>
              </div>
            ) : (
              <div className="flex items-center text-purple-100">
                <CheckCircle className="h-4 w-4 mr-1" />
                <span className="text-sm font-medium">Poll Closed</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Poll Content */}
      <div className="p-6">
        <AnimatePresence>
          <div className="space-y-4">
            {poll.options.map((option, index) => {
              const percentage = getVotePercentage(option.id);
              const isWinning = winningOptionId === option.id && isExpired;
              const isUserVote = userVote === option.id;
              
              return (
                <motion.div
                  key={option.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative border-2 rounded-lg p-4 transition-all duration-300 ${
                    isWinning 
                      ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20' 
                      : isUserVote
                      ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  {/* Option Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      {isWinning && (
                        <Trophy className="h-5 w-5 text-yellow-500 mr-2" />
                      )}
                      {isUserVote && (
                        <CheckCircle className="h-5 w-5 text-blue-500 mr-2" />
                      )}
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {option.text}
                      </h3>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      {/* Vote Count Badge */}
                      <div className="flex items-center bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded-full">
                        <Users className="h-3 w-3 text-gray-600 dark:text-gray-300 mr-1" />
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
                          {votesArray.filter(vote => vote.optionId === option.id).length}
                        </span>
                      </div>
                      
                      {isExpired && (
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          {percentage}%
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  {isExpired && (
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-3">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        transition={{ duration: 1, delay: index * 0.2 }}
                        className={`h-2 rounded-full ${
                          isWinning 
                            ? 'bg-yellow-400' 
                            : 'bg-blue-400'
                        }`}
                      />
                    </div>
                  )}

                  {/* Vote Button */}
                  {!isExpired && !userVote && (
                    <button
                      onClick={() => handleVote(option.id)}
                      disabled={isVoting}
                      className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {isVoting ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ) : (
                        <Vote className="h-4 w-4 mr-2" />
                      )}
                      Vote for this option
                    </button>
                  )}

                  {/* Vote Count - Always visible */}
                  <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                    <Users className="h-4 w-4 mr-1" />
                    <span>{votesArray.filter(vote => vote.optionId === option.id).length} votes</span>
                    {!isExpired && (
                      <motion.div
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="ml-2 w-2 h-2 bg-green-500 rounded-full"
                      />
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </AnimatePresence>

        {/* Poll Status */}
        <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <BarChart3 className="h-5 w-5 text-gray-500 mr-2" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {isExpired ? 'Final Results' : 'Live Results'}
              </span>
              {!isExpired && (
                <motion.div
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="ml-2 w-2 h-2 bg-green-500 rounded-full"
                />
              )}
            </div>
            
            <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center">
                <TrendingUp className="h-4 w-4 mr-1" />
                <span>{totalVotes} total votes</span>
              </div>
              
              {isExpired && winningOptionId && (
                <div className="flex items-center text-yellow-600 dark:text-yellow-400">
                  <Star className="h-4 w-4 mr-1" />
                  <span>Winner: {poll.options.find(opt => opt.id === winningOptionId)?.text}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Live Vote Breakdown */}
          {!isExpired && totalVotes > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
              <div className="grid grid-cols-2 gap-2 text-xs">
                {poll.options.map((option) => {
                  const optionVotes = votesArray.filter(vote => vote.optionId === option.id).length;
                  return (
                    <div key={option.id} className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-400 truncate">{option.text}</span>
                      <span className="font-medium text-gray-800 dark:text-gray-200">{optionVotes}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HobbiesPoll;
